import axios from 'axios'
import { API_BASE, apiErrorHandler, ApiFilter } from '../api'
import Auth from '../auth'
import Employee from '@/services/employees'

const DONATION_URL = `${API_BASE}donations/`
const SUBSCRIBE_URL = `${API_BASE}stripe/subscribe/`
const UNSUBSCRIBE_URL = `${API_BASE}stripe/unsubscribe/`

export default class DonationAPI {
  constructor(cls) {
    this.cls = cls
  }

  static create(cls) {
    return new DonationAPI(cls)
  }

  /*
   * Submit a one-time donation from an authenticated OR anonymous user.
   */
  submitDonation(donation, params = {}) {
    const data = this.cls.toAPI(donation)
    const paramsMap = {
      donorId: ApiFilter.create({ key: 'donor_id' }),
      magicToken: ApiFilter.create({ key: 'magic_token' }),
    }
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
      params: ApiFilter.buildParams(paramsMap, { ...params }),
    }
    return axios
      .post(DONATION_URL, data, options)
      .then(res => this.cls.fromAPI(res.data))
      .catch(apiErrorHandler({ apiName: 'Create Donation API error' }))
  }

  async retrieve(donationId, params = {}) {
    const paramsMap = {
      donorId: ApiFilter.create({ key: 'donor_id' }),
      magicToken: ApiFilter.create({ key: 'magic_token' }),
    }
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
      params: ApiFilter.buildParams(paramsMap, { ...params }),
    }

    const url = `${DONATION_URL}${donationId}/`
    return axios
      .get(url, options)
      .then(response => response.data)
      .then(this.cls.fromAPI)
      .catch(apiErrorHandler({ apiName: 'Retrieve Donation API error' }))
  }

  list({ pagination, filters }) {
    const filtersMap = {
      // Pagination
      page: ApiFilter.create({ key: 'page' }),
      pageSize: ApiFilter.create({ key: 'page_size' }),

      // Sorting
      ordering: ApiFilter.create({ key: 'ordering' }),

      // Filtering
      frequency: ApiFilter.create({ key: 'frequency' }),
      uniqueSubscription: ApiFilter.create({ key: 'unique_subscription' }),
      subscriptionId: ApiFilter.create({ key: 'subscription_id' }),
      magicToken: ApiFilter.create({ key: 'magic_token' }),
      donorId: ApiFilter.create({ key: 'donor_id' }),
    }

    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
      params: ApiFilter.buildParams(filtersMap, { ...pagination, ...filters }),
    }

    const url = DONATION_URL
    return axios
      .get(url, options)
      .then(res => ({
        ...res.data,
        results: res.data.results.map(this.cls.fromAPI),
      }))
      .catch(apiErrorHandler({ apiName: 'Retrieve Donation API error' }))
  }

  /*
   * Start a recurring donation for an authenticated user.
   */
  async startRecurringDonation(amount) {
    const data = { amount: amount }
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }
    return (
      axios
        .post(SUBSCRIBE_URL, data, options)
        .then(response => response.data)
        // Endpoint returns authenticated employee
        .then(Employee.fromAPI)
        .catch(apiErrorHandler({ apiName: 'Start Recurring Donation API error' }))
    )
  }

  /*
   * Cancel all recurring donations for an authenticated user.
   */
  async cancelRecurringDonations(subscriptionId) {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }
    const data = {
      subscription_id: subscriptionId,
    }
    return (
      axios
        .post(UNSUBSCRIBE_URL, data, options)
        .then(response => response.data)
        // Endpoint returns authenticated employee
        .then(Employee.fromAPI)
        .catch(apiErrorHandler({ apiName: 'Cancel Recurring Donation API error' }))
    )
  }
}
